import { useMutation, useQuery } from "@tanstack/react-query";
import type { AccountassetsFetchInterface } from "@dealer-center/ddc-pages-accountassets-interface";

const queryKeyBase = "ddc-pages-accountassets";

export function useAccountassetsAPIInterface({
  searchValue
}: AccountassetsFetchInterface) {
  return useQuery({
    queryKey: [queryKeyBase, { searchValue }],
    queryFn: () => getFunction(searchValue?.trim() || ""),
    enabled: !!searchValue && searchValue.trim().length > 0,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}

const getFunction = async (searchValue: string): Promise<any[]> => {
  const res = await fetch("/landing/nxlambda/accountassets/" + searchValue);
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

export interface PresignedUrlResponse {
  url: string;
  metadata: {
    createdOn: string;
    createdBy: string;
  };
}

export interface FileMetadataResponse {
  found: boolean;
  filePath: string;
  metadata: {
    contentType?: string;
    contentLength?: number;
    lastModified?: string;
    createdOn?: string;
    createdBy?: string;
  } | null;
}

export interface PresignedUrlParams {
  asAccount: string;
  asUser: string;
  path: string;
  locale?: string;
}

/**
 * Get a presigned URL for uploading an asset
 */
export const getPresignedUrl = async ({
  asAccount,
  asUser,
  path,
  locale = "en_US"
}: PresignedUrlParams): Promise<PresignedUrlResponse> => {
  const params = new URLSearchParams({
    asAccount,
    asUser,
    path,
    locale
  });

  const res = await fetch(
    `/landing/nxlambda/accountassets?${params.toString()}`
  );

  if (!res.ok) {
    throw new Error(`Failed to get presigned URL: ${res.statusText}`);
  }

  return res.json();
};

/**
 * Get file metadata from S3
 */
export const getFileMetadata = async ({
  asAccount,
  asUser,
  path,
  locale = "en_US"
}: PresignedUrlParams): Promise<FileMetadataResponse> => {
  const params = new URLSearchParams({
    asAccount,
    asUser,
    locale,
    path,
    action: "metadata"
  });

  const res = await fetch(
    `/landing/nxlambda/accountassets?${params.toString()}`
  );

  if (!res.ok) {
    throw new Error(`Failed to get file metadata: ${res.statusText}`);
  }

  return res.json();
};

/**
 * Hook for deleting an asset
 */
export const useDeleteAsset = () => {
  return useMutation({
    mutationFn: async ({
      asAccount,
      asUser,
      path,
      locale = "en_US"
    }: PresignedUrlParams) => {
      const params = new URLSearchParams({
        asAccount,
        asUser,
        path,
        locale
      });

      const res = await fetch(
        `/landing/nxlambda/accountassets?${params.toString()}`,
        {
          method: "DELETE"
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to delete asset: ${res.statusText}`);
      }

      return res.json();
    }
  });
};
